import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import { DIALOG_PROPERTIES } from "@bca-core/modules/dialog/models/dialog-props";
import { DialogService } from "@bca-core/modules/dialog/services/dialog.service";
import { AvoTrackingService } from "@bca-core/services/avo-tracking.service";
import { DialogIdentifier } from "@bca-shared/models/_enums";
import { IConfirmDialogProps } from "@bca-shared/models/confirm-dialog-props";
import { Observable, Subject } from "rxjs";
import { take, tap } from "rxjs/operators";

@Component({
  selector: "bca-confirm-dialog",
  template: `
    <ns-dialog-content>
      <h2 class="nes-h3 mb-3">{{ props.title }}</h2>
      <div>{{ props.body }}</div>
    </ns-dialog-content>
    <ns-dialog-actions>
      <nes-button
        type="button"
        [cypress]="'dialog_decline'"
        (click)="close()"
        class="mr-2"
        variant="tertiary"
        class="nes-button"
      >
        {{ "BUTTON.CANCEL" | translate }}
      </nes-button>
      <nes-button
        type="button"
        variant="primary"
        [cypress]="'dialog_accept'"
        (click)="accept$.next()"
        class="nes-button"
      >
        {{ "DIALOG.PRODUCT.BUTTON_DELETE_CARD" | translate }}
      </nes-button>
    </ns-dialog-actions>
  `,
  styleUrls: ["confirm-dialog.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialog {
  readonly accept$ = new Subject<void>();
  readonly decline$ = new Subject<void>();

  constructor(
    @Inject(DIALOG_PROPERTIES) public props: IConfirmDialogProps,
    private _dialogService: DialogService,
    private _avoTrackingService: AvoTrackingService
  ) {}

  /**
   * @description Listen to accept button click (only once)
   * @param closeDialog Should the dialog close after accept button click (default: true)
   */
  accept(closeDialog: boolean = true): Observable<void> {
    return this.accept$.asObservable().pipe(
      take(1),
      tap(() => {
        this._avoTrackingService.trackRemoveFromCart(this.props.product);
        return (
          closeDialog && this._dialogService.close(DialogIdentifier.Confirm)
        );
      })
    );
  }

  /**
   * @description Listen to decline button click (only once)
   * @param closeDialog Should the dialog close after decline button click (default: true)
   */
  decline(closeDialog: boolean = true): Observable<void> {
    return this.decline$.asObservable().pipe(
      take(1),
      tap(
        () => closeDialog && this._dialogService.close(DialogIdentifier.Confirm)
      )
    );
  }

  /**
   * @description Close dialog
   */
  close(): void {
    this.decline$.next();
    this._dialogService.close(DialogIdentifier.Confirm);
  }
}
